export interface CanonicalStatuteSection {
  id: string;
  label: string;
  title: string;
  /** Short heading (provision title only); full trail is in `title` when from v2 flatten. */
  shortTitle?: string;
  /** One-line sidebar label (legacy / tooltip). */
  tocLine?: string;
  /** Top structural bucket, e.g. Preliminary, Part I, First Schedule (sidebar + reader, muted). */
  tocPartLabel?: string;
  /** Secondary structural bucket (used by QSO), e.g. Chapter I — Preliminary. */
  tocChapterLabel?: string;
  /** Optional tertiary bucket (used by QSO), e.g. ADMISSIONS. */
  tocHeadingLabel?: string;
  /** e.g. Section 3, Order XXI, Rule 58 */
  tocSectionBadge?: string;
  /** 1-based position in the flat reading list (sidebar index). */
  tocListIndex?: number;
  content?: string;
  order?: number;
  /** Sub-clause / list marker for StatuteRenderer, e.g. a, b, i */
  letter?: string;
  /** Subsection marker for StatuteRenderer, e.g. 1, 2 */
  number?: string;
  subsections?: CanonicalStatuteSection[];
  clauses?: CanonicalStatuteSection[];
  provisos?: Array<string | CanonicalStatuteSection>;
  explanations?: Array<string | CanonicalStatuteSection>;
}

export interface CanonicalStatuteContent {
  schemaVersion: 'v1' | 'v2';
  sections: CanonicalStatuteSection[];
}

function normalizeSection(input: unknown, index: number): CanonicalStatuteSection {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const id = raw.id != null && String(raw.id).trim() ? String(raw.id) : `section-${index + 1}`;
  const label = raw.label != null && String(raw.label).trim() ? String(raw.label) : `SECTION ${index + 1}`;
  const rawShort =
    raw.shortTitle != null && String(raw.shortTitle).trim()
      ? String(raw.shortTitle).trim()
      : raw.short_title != null && String(raw.short_title).trim()
        ? String(raw.short_title).trim()
        : '';
  const titleFromRaw = raw.title != null && String(raw.title).trim() ? String(raw.title).trim() : '';
  const title = titleFromRaw || rawShort || label;
  const shortTitle = rawShort || undefined;
  const content = typeof raw.content === 'string' ? raw.content : undefined;
  const order = typeof raw.order === 'number' && !Number.isNaN(raw.order) ? raw.order : index;

  const normalizeList = (value: unknown): CanonicalStatuteSection[] => {
    if (!Array.isArray(value)) return [];
    return value.map((item, i) => normalizeSection(item, i));
  };

  const subsections = normalizeList(raw.subsections ?? raw.sub_sections ?? raw.subSections);
  const clauses = normalizeList(raw.clauses);

  const tocPart =
    raw.tocPartLabel != null && String(raw.tocPartLabel).trim()
      ? String(raw.tocPartLabel).trim()
      : raw.toc_part_label != null && String(raw.toc_part_label).trim()
        ? String(raw.toc_part_label).trim()
        : undefined;
  const tocChapter =
    raw.tocChapterLabel != null && String(raw.tocChapterLabel).trim()
      ? String(raw.tocChapterLabel).trim()
      : raw.toc_chapter_label != null && String(raw.toc_chapter_label).trim()
        ? String(raw.toc_chapter_label).trim()
        : undefined;
  const tocBadge =
    raw.tocSectionBadge != null && String(raw.tocSectionBadge).trim()
      ? String(raw.tocSectionBadge).trim()
      : raw.toc_section_badge != null && String(raw.toc_section_badge).trim()
        ? String(raw.toc_section_badge).trim()
        : undefined;

  return {
    id,
    label,
    title,
    ...(shortTitle ? { shortTitle } : {}),
    content,
    order,
    ...(tocPart ? { tocPartLabel: tocPart } : {}),
    ...(tocChapter ? { tocChapterLabel: tocChapter } : {}),
    ...(tocBadge ? { tocSectionBadge: tocBadge } : {}),
    ...(subsections.length ? { subsections } : {}),
    ...(clauses.length ? { clauses } : {}),
    ...(Array.isArray(raw.provisos) ? { provisos: raw.provisos as Array<string | CanonicalStatuteSection> } : {}),
    ...(Array.isArray(raw.explanations) ? { explanations: raw.explanations as Array<string | CanonicalStatuteSection> } : {}),
  };
}

export function parseCanonicalStatuteContent(rawText: string): CanonicalStatuteContent | null {
  const raw = rawText.trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (Array.isArray(parsed)) {
      return {
        schemaVersion: 'v1',
        sections: parsed.map((item, i) => normalizeSection(item, i)),
      };
    }

    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      if (obj.schemaVersion === 'v2' && Array.isArray(obj.structure)) {
        const flattened: CanonicalStatuteSection[] = [];
        const walk = (node: Record<string, unknown>, trail: string[]) => {
          const label = String(node.label ?? '');
          const title = String(node.title ?? label);
          const nextTrail = label ? [...trail, label] : trail;
          if (Array.isArray(node.children)) {
            (node.children as unknown[]).forEach((child) => {
              if (child && typeof child === 'object') walk(child as Record<string, unknown>, nextTrail);
            });
            return;
          }
          const leafId = String(node.id ?? `section-${flattened.length + 1}`);
          const contentDisplay = typeof node.content_display === 'string' ? node.content_display : '';
          flattened.push({
            id: leafId,
            label,
            title,
            content: contentDisplay || (nextTrail.length ? nextTrail.join(' > ') : title),
            order: flattened.length,
          });
        };
        (obj.structure as unknown[]).forEach((node) => {
          if (node && typeof node === 'object') walk(node as Record<string, unknown>, []);
        });
        return {
          schemaVersion: 'v2',
          sections: flattened,
        };
      }
      if (Array.isArray(obj.sections)) {
        return {
          schemaVersion: 'v1',
          sections: obj.sections.map((item, i) => normalizeSection(item, i)),
        };
      }
    }
  } catch {
    return null;
  }

  return null;
}

function stripParenLabel(label: string): string {
  const t = label.trim();
  const m = t.match(/^\(([^)]+)\)$/);
  return m ? m[1] : t;
}

function mapV2Proviso(p: unknown): string | CanonicalStatuteSection {
  if (typeof p === 'string') return p;
  if (p && typeof p === 'object') {
    const o = p as Record<string, unknown>;
    const lab = o.label != null ? String(o.label) : '';
    const text = o.text != null ? String(o.text) : '';
    return { id: 'proviso', label: lab || 'Proviso', title: lab || 'Proviso', content: text || lab };
  }
  return '';
}

function mapV2Explanation(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    const lab = o.label != null ? String(o.label) : '';
    const text = o.text != null ? String(o.text) : '';
    return [lab, text].filter(Boolean).join(': ').trim() || text;
  }
  return '';
}

function mapV2Clause(c: unknown, idx: number): CanonicalStatuteSection {
  const o = (c && typeof c === 'object' ? c : {}) as Record<string, unknown>;
  const label = o.label != null ? String(o.label) : '';
  const key = stripParenLabel(label) || String(o.id || idx);
  let text = o.text != null ? String(o.text) : '';
  const subClauses = o.subClauses;
  if (Array.isArray(subClauses) && subClauses.length) {
    const tail = subClauses
      .map((sc, j) => {
        const so = sc as Record<string, unknown>;
        return so.text != null ? String(so.text) : '';
      })
      .filter(Boolean)
      .join('\n');
    if (tail) text = text ? `${text}\n${tail}` : tail;
  }
  return {
    id: String(o.id || `cl-${idx}`),
    label: label || key,
    title: label || key,
    content: text,
    letter: key,
  };
}

function mapV2Subsection(sub: unknown, idx: number): CanonicalStatuteSection {
  const o = (sub && typeof sub === 'object' ? sub : {}) as Record<string, unknown>;
  const label = o.label != null ? String(o.label) : '';
  const num = stripParenLabel(label) || String(idx + 1);
  const text = o.text != null ? String(o.text) : '';
  const clauses = Array.isArray(o.clauses) ? o.clauses.map((c, i) => mapV2Clause(c, i)) : [];
  const provisos = Array.isArray(o.provisos) ? o.provisos.map(mapV2Proviso) : [];
  const explanations = Array.isArray(o.explanations) ? o.explanations.map(mapV2Explanation).filter(Boolean) : [];

  const out: CanonicalStatuteSection = {
    id: String(o.id || `ss-${idx}`),
    label: label || `(${num})`,
    title: label || `(${num})`,
    content: text,
    number: num,
  };
  if (clauses.length) out.clauses = clauses;
  if (provisos.length) out.provisos = provisos;
  if (explanations.length) out.explanations = explanations;
  return out;
}

function previewTocPrefix(id: string): string | null {
  let m = id.match(/^s-(\d+[A-Za-z]*)(?:~|$)/);
  if (m) return m[1];
  m = id.match(/^art-(\d+[A-Za-z]*)(?:~|$)/i);
  if (m) return m[1];
  m = id.match(/^ord-([IVXLCDM]+)-r-(\d+[A-Za-z]*)/i);
  if (m) return `${m[1]} · R${m[2]}`;
  if (/^preamble/i.test(id)) return 'Preamble';
  m = id.match(/^app-([A-H])-form-(\d+)/i);
  if (m) return `App ${m[1]} · F${m[2]}`;
  m = id.match(/^sch-(\d+)-s-(\d+[A-Za-z]*)/i);
  if (m) return `Sch ${m[1]} · ${m[2]}`;
  return null;
}

function buildPreviewTocLine(
  id: string,
  ancestors: string[],
  shortTitle: string,
  fallbackOrdinal: number,
): string {
  const trail =
    ancestors.length > 0 ? `${ancestors.join(' › ')} › ${shortTitle}` : shortTitle;
  const prefix = previewTocPrefix(id);
  if (prefix) return `${prefix}: ${trail}`;
  return `${fallbackOrdinal}: ${trail}`;
}

/** Muted “part” line: first ancestor for sections, last for rules/forms (Order / Appendix context). */
function tocPartLabelFor(nodeType: string, ancestors: string[]): string | undefined {
  if (!ancestors.length) return undefined;
  if (nodeType === 'rule' || nodeType === 'form') {
    return ancestors[ancestors.length - 1];
  }
  return ancestors[0];
}

function tocSectionBadgeFromId(id: string, nodeType: string): string | null {
  const s = id.match(/^s-(\d+[A-Za-z]*)(?:~|$)/i);
  if (s) return `Section ${s[1]}`;
  const a = id.match(/^art-(\d+[A-Za-z]*)(?:~|$)/i);
  if (a) return `Article ${a[1]}`;
  const r = id.match(/^ord-([IVXLCDM]+)-r-(\d+[A-Za-z]*)/i);
  if (r) return `Order ${r[1]}, Rule ${r[2]}`;
  if (/^preamble/i.test(id)) return 'Preamble';
  const f = id.match(/^app-([A-H])-form-(\d+)/i);
  if (f) return `Appendix ${f[1]}, Form ${f[2]}`;
  const sch = id.match(/^sch-(\d+)-s-(\d+[A-Za-z]*)/i);
  if (sch) return `Schedule ${sch[1]}, Section ${sch[2]}`;
  if (nodeType === 'order') return 'Order';
  return null;
}

function structuralHeading(node: Record<string, unknown>, fallback: string): string {
  const structuralType = String(node.type || '').trim();
  const label = String(node.label || '').trim();
  const title = String(node.title || '').trim();
  /** Keep sidebar / trail buckets compact: do not merge enactment text into the Preamble ancestor. */
  if (structuralType === 'preamble' || structuralType === 'preliminary') {
    if (label) return label;
    if (title) return title;
    return fallback;
  }
  if (label && title && label.toLowerCase() !== title.toLowerCase()) return `${label} ${title}`;
  if (label) return label;
  if (title) return title;
  return fallback;
}

function attachTocMeta(
  out: CanonicalStatuteSection,
  id: string,
  nodeType: string,
  ancestors: string[],
  listIndex: number,
) {
  const part = tocPartLabelFor(nodeType, ancestors);
  if (part) out.tocPartLabel = part;
  const isProvision = nodeType === 'article' || nodeType === 'section' || /^art-/i.test(id) || /^s-/i.test(id);
  if (isProvision && ancestors.length > 0) {
    // With part: [Part, Chapter, Heading?]
    // Without part: [Chapter, Heading?]
    out.tocChapterLabel = ancestors.length > 1 ? ancestors[1] : ancestors[0];
  }
  if (isProvision && ancestors.length > 1) {
    out.tocHeadingLabel = ancestors[ancestors.length - 1];
    if (out.tocHeadingLabel === out.tocChapterLabel) out.tocHeadingLabel = undefined;
  }
  const badge = tocSectionBadgeFromId(id, nodeType);
  if (badge) out.tocSectionBadge = badge;
  out.tocListIndex = listIndex;
}

function normalizeV2Leaf(
  node: Record<string, unknown>,
  ancestors: string[],
  order: number,
): CanonicalStatuteSection {
  const id = String(node.id ?? `leaf-${order}`);
  const label = String(node.label ?? id);
  const shortTitle = String(node.title ?? label);
  const nodeType = String(node.type || 'section');
  const subsectionsRaw = node.subsections;
  const subsections = Array.isArray(subsectionsRaw)
    ? subsectionsRaw.map((s, i) => mapV2Subsection(s, i))
    : [];

  const provisosTop = Array.isArray(node.provisos) ? node.provisos.map(mapV2Proviso) : [];
  const explanationsTop = Array.isArray(node.explanations) ? node.explanations.map(mapV2Explanation).filter(Boolean) : [];

  const trail = ancestors.length > 0 ? `${ancestors.join(' › ')} › ${shortTitle}` : shortTitle;
  const out: CanonicalStatuteSection = {
    id,
    label,
    title: trail,
    shortTitle,
    tocLine: buildPreviewTocLine(id, ancestors, shortTitle, order + 1),
    order,
  };
  attachTocMeta(out, id, nodeType, ancestors, order + 1);
  const heading = typeof node.heading === 'string' ? node.heading.trim() : '';
  if (heading) out.tocHeadingLabel = heading;
  if (subsections.length) out.subsections = subsections;
  if (provisosTop.length) out.provisos = provisosTop;
  if (explanationsTop.length) out.explanations = explanationsTop;

  const contentDisplay = typeof node.content_display === 'string' ? node.content_display : '';
  if (contentDisplay && !subsections.length) out.content = contentDisplay;

  if (!out.subsections?.length && !out.content?.trim()) {
    out.content = shortTitle;
  }

  return out;
}

/**
 * Depth-first flatten of LexGat rich CPC JSON (`schemaVersion` v2, `structure` with subsections / children)
 * into preview/library-shaped sections with nested subsections for StatuteRenderer.
 */
export function lexgatV2StructureToPreviewSections(structure: unknown): CanonicalStatuteSection[] {
  if (!Array.isArray(structure)) return [];

  const out: CanonicalStatuteSection[] = [];
  let seq = 0;

  const visit = (node: Record<string, unknown>, ancestors: string[]) => {
    const type = String(node.type || '');
    const rawKids = node.children;
    const kids = Array.isArray(rawKids) ? (rawKids as Record<string, unknown>[]) : [];

    if (
      type === 'part' ||
      type === 'preliminary' ||
      type === 'preamble' ||
      type === 'schedule' ||
      type === 'appendix' ||
      type === 'chapter' ||
      type === 'heading'
    ) {
      if (type === 'preamble' && kids.length === 0) {
        const sid = String(node.id || `preamble-${seq}`);
        const st = String(node.title || node.label || 'Preamble');
        const tr = ancestors.length > 0 ? `${ancestors.join(' › ')} › ${st}` : st;
        const row: CanonicalStatuteSection = {
          id: sid,
          label: 'Preamble',
          title: tr,
          shortTitle: st,
          tocLine: buildPreviewTocLine(sid, ancestors, st, seq + 1),
          content: st,
          order: seq,
        };
        attachTocMeta(row, sid, 'section', ancestors, seq + 1);
        out.push(row);
        seq += 1;
        return;
      }
      const lab = structuralHeading(node, type);
      kids.forEach((c) => visit(c, [...ancestors, lab]));
      return;
    }

    if (type === 'order') {
      const lab = structuralHeading(node, 'Order');
      if (kids.length > 0) {
        kids.forEach((c) => visit(c, [...ancestors, lab]));
      } else {
        const sid = String(node.id || `ord-empty-${seq}`);
        const st = String(node.title || lab);
        const tr = ancestors.length > 0 ? `${ancestors.join(' › ')} › ${st}` : st;
        const ordRow: CanonicalStatuteSection = {
          id: sid,
          label: lab || String(node.id || 'Order'),
          title: tr,
          shortTitle: st,
          tocLine: buildPreviewTocLine(sid, ancestors, st, seq + 1),
          content: '',
          order: seq,
        };
        attachTocMeta(ordRow, sid, 'order', ancestors, seq + 1);
        out.push(ordRow);
        seq += 1;
      }
      return;
    }

    if (type === 'section' || type === 'article' || type === 'rule' || type === 'form') {
      out.push(normalizeV2Leaf(node, ancestors, seq++));
      return;
    }

    kids.forEach((c) => visit(c, ancestors));
  };

  (structure as Record<string, unknown>[]).forEach((n) => visit(n, []));
  return out;
}

export function parseLexgatStatuteJsonPayload(raw: unknown): CanonicalStatuteSection[] | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (obj.schemaVersion === 'v2' && Array.isArray(obj.structure)) {
    return lexgatV2StructureToPreviewSections(obj.structure);
  }
  // Legacy nested payload support (e.g. title -> parts -> chapters -> sections).
  if (Array.isArray(obj.parts)) {
    const out: CanonicalStatuteSection[] = [];
    let seq = 0;
    const parts = obj.parts as Array<Record<string, unknown>>;
    parts.forEach((part, pIdx) => {
      const partTitle = String(part.title || `Part ${pIdx + 1}`).trim();
      const chapters = Array.isArray(part.chapters) ? (part.chapters as Array<Record<string, unknown>>) : [];
      chapters.forEach((chapter, cIdx) => {
        const chapterTitle = String(chapter.title || `Chapter ${cIdx + 1}`).trim();
        const sections = Array.isArray(chapter.sections) ? (chapter.sections as Array<Record<string, unknown>>) : [];
        sections.forEach((s, sIdx) => {
          const rawNum = String(s.section || '').trim();
          const rawTitle = String(s.title || '').trim();
          const content = typeof s.content === 'string' ? s.content : '';
          const shortTitle = rawTitle || `Section ${rawNum || sIdx + 1}`;
          const id = rawNum ? `s-${rawNum}` : `s-${seq + 1}`;
          const label = rawNum ? `Section ${rawNum}` : `Section ${seq + 1}`;
          const row: CanonicalStatuteSection = {
            id,
            label,
            title: shortTitle,
            shortTitle,
            content: content || shortTitle,
            order: seq,
            tocLine: `${rawNum || seq + 1}: ${partTitle} › ${chapterTitle} › ${shortTitle}`,
            tocPartLabel: partTitle,
            tocChapterLabel: chapterTitle,
            tocSectionBadge: rawNum ? `Section ${rawNum}` : `Section ${seq + 1}`,
            tocListIndex: seq + 1,
          };
          out.push(row);
          seq += 1;
        });
      });
    });
    return out;
  }
  // Legacy payload support (e.g. title -> chapters -> sections).
  if (Array.isArray(obj.chapters)) {
    const out: CanonicalStatuteSection[] = [];
    let seq = 0;
    const chapters = obj.chapters as Array<Record<string, unknown>>;
    chapters.forEach((chapter, cIdx) => {
      const chapterTitle = String(chapter.title || `Chapter ${cIdx + 1}`).trim();
      const sections = Array.isArray(chapter.sections) ? (chapter.sections as Array<Record<string, unknown>>) : [];
      sections.forEach((s, sIdx) => {
        const rawNum = String(s.section || '').trim();
        const rawTitle = String(s.title || '').trim();
        const content = typeof s.content === 'string' ? s.content : '';
        const shortTitle = rawTitle || `Section ${rawNum || sIdx + 1}`;
        const id = rawNum ? `s-${rawNum}` : `s-${seq + 1}`;
        const label = rawNum ? `Section ${rawNum}` : `Section ${seq + 1}`;
        const row: CanonicalStatuteSection = {
          id,
          label,
          title: shortTitle,
          shortTitle,
          content: content || shortTitle,
          order: seq,
          tocLine: `${rawNum || seq + 1}: ${chapterTitle} › ${shortTitle}`,
          tocPartLabel: chapterTitle,
          tocChapterLabel: chapterTitle,
          tocSectionBadge: rawNum ? `Section ${rawNum}` : `Section ${seq + 1}`,
          tocListIndex: seq + 1,
        };
        out.push(row);
        seq += 1;
      });
    });
    return out;
  }
  if (Array.isArray(obj.sections)) {
    return obj.sections.map((item, i) => normalizeSection(item, i));
  }
  return null;
}

/**
 * Single entry point for Preview + Library: same JSON string → same section list
 * (before UI-specific {@link normalizePreviewSections} ordering tweaks).
 */
export function parseStatuteTextForReader(rawText: string): CanonicalStatuteSection[] {
  const raw = rawText.trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    const fromLexgat = parseLexgatStatuteJsonPayload(parsed);
    if (fromLexgat?.length) return fromLexgat;
  } catch {
    // Fall through to legacy string parser
  }
  const legacy = parseCanonicalStatuteContent(raw);
  return legacy?.sections?.length ? legacy.sections : [];
}
